import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ShieldCheck, Copy, Smartphone, Banknote } from "lucide-react";
import SEO from "../components/SEO";
import { useCart } from "../contexts/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const UPI_ID = "9711184480@ptsbi";
const MERCHANT_NAME = "The Dripchexk";

const buildUpiUrl = (amount, note) =>
  `upi://pay?pa=${encodeURIComponent(UPI_ID)}&pn=${encodeURIComponent(
    MERCHANT_NAME
  )}&am=${amount}&cu=INR&tn=${encodeURIComponent(note)}`;

const Checkout = () => {
  const { items, subtotal, shipping, total, clear } = useCart();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [payStep, setPayStep] = useState("methods"); // "methods" | "upi"
  const [utr, setUtr] = useState("");
  const [form, setForm] = useState({
    full_name: "", address: "", city: "", pincode: "", phone: "", email: "", notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  if (items.length === 0) {
    return (
      <section className="max-w-2xl mx-auto px-6 py-32 text-center">
        <SEO title="Checkout" />
        <p className="font-display text-4xl">YOUR BAG IS EMPTY<span className="text-[#FF69B4]">.</span></p>
        <Link to="/collections" className="mt-6 inline-block tiny-link border-b border-white pb-1 hover:text-[#FF69B4] hover:border-[#FF69B4]" data-testid="empty-shop-link">Shop the drop →</Link>
      </section>
    );
  }

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const validateStep1 = () => {
    const req = ["full_name", "address", "city", "pincode", "phone", "email"];
    for (const k of req) if (!form[k].trim()) { toast.error("Please fill all fields."); return false; }
    if (!/^\d{6}$/.test(form.pincode)) { toast.error("Pincode must be 6 digits."); return false; }
    if (!/^\d{10}$/.test(form.phone.replace(/\D/g, ""))) { toast.error("Phone must be 10 digits."); return false; }
    if (!/.+@.+\..+/.test(form.email)) { toast.error("Invalid email."); return false; }
    // Fire-and-forget: track this as a pending checkout so we can email a
    // recovery coupon if they don't finish payment within 30 min.
    axios.post(`${API}/checkout/pending`, {
      full_name: form.full_name,
      email: form.email,
      phone: form.phone,
      total,
      items: items.map((i) => ({
        product_id: i.slug, product_name: i.name, size: i.size, quantity: i.quantity, price: i.price,
      })),
    }).catch(() => {});
    return true;
  };

  const placeOrder = async (method, utrValue = null) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        payment_method: method,
        utr: utrValue,
        items: items.map((i) => ({
          product_id: i.slug, product_name: i.name, size: i.size, quantity: i.quantity, price: i.price,
        })),
        subtotal, shipping, total,
      };
      const res = await axios.post(`${API}/orders`, payload);
      const order = res.data;
      clear();
      navigate(`/order/${order.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't place order. Try again.");
      setSubmitting(false);
    }
  };

  const utrValid = /^\d{12}$/.test(utr);

  const previewUpiUrl = buildUpiUrl(total, "The Dripchexk order");
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=280x280&data=${encodeURIComponent(previewUpiUrl)}&bgcolor=17-17-17&color=255-255-255&qzone=2`;

  return (
    <>
      <SEO title="Checkout" />
      <section className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-12 py-12">
        <p className="tiny-link text-[#FF69B4]">Checkout · Step {step} of 2</p>
        <h1 className="font-display text-4xl sm:text-5xl mt-3 tracking-[0.03em]">
          {step === 1 ? "DELIVERY" : "PAYMENT"}<span className="text-[#FF69B4]">.</span>
        </h1>

        <div className="grid lg:grid-cols-3 gap-10 mt-10">
          <div className="lg:col-span-2">
            {step === 1 && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
                <Field label="Full Name" v={form.full_name} onC={(v) => update("full_name", v)} testid="checkout-name" />
                <Field label="Email" type="email" v={form.email} onC={(v) => update("email", v)} testid="checkout-email" />
                <Field label="Phone (10 digits)" v={form.phone} onC={(v) => update("phone", v)} testid="checkout-phone" />
                <Field label="Address" v={form.address} onC={(v) => update("address", v)} multi testid="checkout-address" />
                <div className="grid grid-cols-2 gap-4">
                  <Field label="City" v={form.city} onC={(v) => update("city", v)} testid="checkout-city" />
                  <Field label="Pincode" v={form.pincode} onC={(v) => update("pincode", v)} testid="checkout-pincode" />
                </div>
                <Field label="Order notes (optional)" v={form.notes} onC={(v) => update("notes", v)} multi testid="checkout-notes" />
                <button
                  data-testid="step-next-1"
                  onClick={() => validateStep1() && setStep(2)}
                  className="pink-cta tiny-link px-8 py-4"
                >
                  Continue to Payment →
                </button>
              </motion.div>
            )}

            {step === 2 && payStep === "methods" && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
                <div className="border border-[#222] p-5 bg-[#0a0a0a]">
                  <p className="tiny-link text-white/40 mb-2">Deliver to</p>
                  <p className="text-sm font-medium">{form.full_name}</p>
                  <p className="text-sm text-white/70">{form.address}, {form.city} - {form.pincode}</p>
                  <p className="text-sm text-white/70">{form.phone} · {form.email}</p>
                  <button
                    onClick={() => setStep(1)}
                    data-testid="edit-address"
                    className="mt-3 tiny-link text-[#FF69B4] hover:text-white"
                  >
                    ← Edit address
                  </button>
                </div>

                <p className="tiny-link text-[#FF69B4] pt-2">Choose payment method</p>

                {/* UPI — goes to payment instruction screen, order NOT placed yet */}
                <button
                  data-testid="pay-upi-now"
                  onClick={() => setPayStep("upi")}
                  disabled={submitting}
                  className="group w-full border border-[#222] hover:border-[#FF69B4] bg-[#0a0a0a] p-5 text-left transition-colors disabled:opacity-50"
                >
                  <div className="flex items-center gap-4">
                    <Smartphone size={22} className="text-[#FF69B4]" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold tracking-wide">Pay ₹{total} via UPI</p>
                      <p className="text-xs text-white/50 mt-1">
                        Google Pay · PhonePe · Paytm · BHIM — pay to <span className="text-white">{UPI_ID}</span>, then confirm with your UTR
                      </p>
                    </div>
                    <span className="tiny-link text-white/50 group-hover:text-[#FF69B4]">Continue →</span>
                  </div>
                </button>

                {/* COD button */}
                <button
                  data-testid="pay-cod-now"
                  onClick={() => placeOrder("COD")}
                  disabled={submitting}
                  className="group w-full border border-[#222] hover:border-[#FF69B4] bg-[#0a0a0a] p-5 text-left transition-colors disabled:opacity-50"
                >
                  <div className="flex items-center gap-4">
                    <Banknote size={22} className="text-white/70 group-hover:text-[#FF69B4]" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold tracking-wide">Cash on Delivery</p>
                      <p className="text-xs text-white/50 mt-1">Pay in cash at your doorstep when the order arrives.</p>
                    </div>
                    <span className="tiny-link text-white/50 group-hover:text-[#FF69B4]">Place Order →</span>
                  </div>
                </button>

                {submitting && (
                  <p className="text-center text-white/60 text-sm tracking-widest uppercase mt-2 animate-pulse">
                    Placing order...
                  </p>
                )}
              </motion.div>
            )}

            {step === 2 && payStep === "upi" && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-5" data-testid="upi-payment-screen">
                <button
                  onClick={() => { setPayStep("methods"); setUtr(""); }}
                  data-testid="upi-back"
                  className="tiny-link text-[#FF69B4] hover:text-white"
                >
                  ← Back to payment methods
                </button>

                <div className="border border-[#FF69B4]/40 bg-[#0a0a0a] p-6 sm:p-8">
                  <p className="tiny-link text-[#FF69B4]">Step 1 — Pay ₹{total}</p>
                  <p className="text-sm text-white/70 mt-3 leading-relaxed">
                    Transfer the exact amount to this UPI ID on any payment app — GPay, PhonePe, Paytm, or any UPI app. Then enter your UTR/Transaction ID below to confirm payment.
                  </p>

                  {/* UPI ID — large, bold, with copy button */}
                  <div className="mt-6 flex flex-wrap items-center gap-3">
                    <p className="text-2xl sm:text-3xl font-bold font-mono select-all break-all" data-testid="upi-id">{UPI_ID}</p>
                    <button
                      onClick={() => { navigator.clipboard.writeText(UPI_ID); toast.success("UPI ID copied."); }}
                      data-testid="copy-upi"
                      className="flex items-center gap-2 border border-white/20 hover:border-[#FF69B4] hover:text-[#FF69B4] px-4 py-2.5 text-xs tracking-[0.2em] uppercase transition-colors"
                    >
                      <Copy size={14} /> Copy
                    </button>
                  </div>
                  <p className="text-white/50 text-xs mt-2">Amount to pay: <b className="text-white text-base">₹{total}</b> (exact)</p>

                  <div className="mt-6 flex flex-col sm:flex-row gap-6 items-start">
                    <div>
                      <img
                        src={qrSrc}
                        alt="UPI QR Code"
                        className="w-[200px] h-[200px] bg-[#0a0a0a] border border-[#222]"
                        data-testid="upi-qr"
                      />
                      <p className="text-[10px] text-white/40 mt-2 text-center w-[200px]">Scan with any UPI app</p>
                    </div>
                    <div className="flex-1 text-xs text-white/60 leading-relaxed space-y-2">
                      <p>1. Open GPay / PhonePe / Paytm / any UPI app.</p>
                      <p>2. Scan the QR or pay to the UPI ID above.</p>
                      <p>3. Pay exactly <b className="text-white">₹{total}</b>.</p>
                      <p>4. Copy the 12-digit UTR / Transaction ID from the payment success screen.</p>
                      <p>5. Enter it below and place your order.</p>
                      <a
                        href={buildUpiUrl(total, "The Dripchexk order")}
                        data-testid="open-upi-app"
                        className="inline-block mt-2 border border-white/20 hover:border-[#FF69B4] hover:text-[#FF69B4] px-4 py-2.5 tracking-[0.2em] uppercase transition-colors"
                      >
                        On mobile? Tap to open UPI app →
                      </a>
                    </div>
                  </div>
                </div>

                <div className="border border-[#222] bg-[#0a0a0a] p-6 sm:p-8">
                  <p className="tiny-link text-[#FF69B4]">Step 2 — Confirm payment</p>
                  <label className="block mt-4">
                    <span className="tiny-link text-white/40">UTR / Transaction ID (12 digits)</span>
                    <input
                      data-testid="utr-input"
                      inputMode="numeric"
                      maxLength={12}
                      value={utr}
                      onChange={(e) => setUtr(e.target.value.replace(/\D/g, "").slice(0, 12))}
                      placeholder="e.g. 425689731254"
                      className="mt-1 w-full bg-transparent border border-[#222] px-3 py-3.5 text-base font-mono tracking-widest focus:outline-none focus:border-[#FF69B4] transition-colors"
                    />
                  </label>
                  <p className="text-[11px] text-white/40 mt-2">
                    {utrValid ? "✓ UTR looks good." : `Find it on your payment app's success screen or transaction history. ${utr.length}/12 digits.`}
                  </p>
                  <button
                    data-testid="place-upi-order"
                    onClick={() => placeOrder("UPI", utr)}
                    disabled={!utrValid || submitting}
                    className="mt-5 w-full pink-cta tiny-link py-4 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {submitting ? "Placing order..." : utrValid ? `Place Order — ₹${total} Paid` : "Enter UTR to activate"}
                  </button>
                  <p className="text-[11px] text-white/40 mt-3 text-center">
                    We verify every payment manually before dispatch. Wrong or fake UTRs are rejected.
                  </p>
                </div>
              </motion.div>
            )}
          </div>

          {/* Summary */}
          <aside className="lg:sticky lg:top-28 self-start border border-[#222] p-6 bg-[#0a0a0a]">
            <p className="tiny-link text-white/40">Order Summary</p>
            <ul className="mt-4 space-y-3 max-h-72 overflow-y-auto">
              {items.map((i) => (
                <li key={i.key} className="flex gap-3 text-sm">
                  <img src={i.image} alt={i.name} className="w-14 h-16 object-cover bg-white/5" />
                  <div className="flex-1">
                    <p className="font-medium">{i.name}</p>
                    <p className="text-white/50 text-xs">{i.size} × {i.quantity}</p>
                  </div>
                  <p>₹{i.price * i.quantity}</p>
                </li>
              ))}
            </ul>
            <div className="border-t border-[#222] mt-4 pt-4 space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-white/60">Subtotal</span><span>₹{subtotal}</span></div>
              <div className="flex justify-between"><span className="text-white/60">Shipping</span><span>{shipping === 0 ? "Free" : `₹${shipping}`}</span></div>
              <div className="flex justify-between font-medium pt-2 border-t border-[#222] mt-2"><span>Total</span><span>₹{total}</span></div>
            </div>
            <div className="flex items-center gap-2 mt-5 text-xs text-white/50">
              <ShieldCheck size={14} className="text-[#FF69B4]" /> Secure encrypted checkout
            </div>
          </aside>
        </div>
      </section>
    </>
  );
};

const Field = ({ label, type = "text", v, onC, multi = false, testid }) => (
  <label className="block">
    <span className="tiny-link text-white/40">{label}</span>
    {multi ? (
      <textarea
        data-testid={testid}
        value={v}
        onChange={(e) => onC(e.target.value)}
        rows={3}
        className="mt-1 w-full bg-transparent border border-[#222] px-3 py-3 text-sm focus:outline-none focus:border-[#FF69B4] transition-colors"
      />
    ) : (
      <input
        data-testid={testid}
        type={type}
        value={v}
        onChange={(e) => onC(e.target.value)}
        className="mt-1 w-full bg-transparent border border-[#222] px-3 py-3 text-sm focus:outline-none focus:border-[#FF69B4] transition-colors"
      />
    )}
  </label>
);

export default Checkout;
